import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRoles } from "@/hooks/useUserRoles";
import type { UserRole } from "@/hooks/useUserRole";
import { canAccessProtectedRole, hasApprovedRole } from "@/lib/userRoles";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RouteFallback } from "@/components/RouteFallback";

type ProtectedRouteProps = {
  children: React.ReactNode;
  allowedRole: UserRole;
};

export const ProtectedRoute = ({ children, allowedRole }: ProtectedRouteProps) => {
  const { roleRows, role, isPartner, isBanned, loading } = useUserRoles();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sessionVerified, setSessionVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const access = allowedRole
    ? canAccessProtectedRole(roleRows, allowedRole, { isPartner })
    : { allowed: false, approvalStatus: null };

  useEffect(() => {
    if (loading) return;

    if (isBanned && role !== "admin") {
      navigate("/banned");
      return;
    }

    if (roleRows.length > 0) {
      setSessionVerified(true);

      if (allowedRole === "admin") {
        if (role !== "admin") {
          toast({
            variant: "destructive",
            title: "Access Restricted",
            description: "Sorry, this part of the site is restricted to administrators",
          });
          navigate("/");
        }
        return;
      }

      if (!access.allowed) {
        if (hasApprovedRole(roleRows, "tester") && allowedRole !== "tester") {
          navigate("/tester", { replace: true });
          return;
        }
        toast({
          variant: "destructive",
          title: "Access Restricted",
          description: "Sorry, this part of the site is restricted",
        });
        navigate("/");
        return;
      }

      if (allowedRole === "caterer" && access.approvalStatus === "pending") {
        return;
      }

      if (allowedRole === "pfa") {
        if (access.approvalStatus === "rejected") {
          navigate("/pending-approval");
        }
        return;
      }

      if (access.approvalStatus !== "approved") {
        navigate("/pending-approval");
      }
      return;
    }

    if (!verifying) {
      setVerifying(true);
      void supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) {
          navigate("/auth/signin", { replace: true });
        }
        setVerifying(false);
      });
    }
  }, [
    access.allowed,
    access.approvalStatus,
    roleRows.length,
    role,
    isBanned,
    loading,
    allowedRole,
    navigate,
    toast,
    verifying,
  ]);

  if ((loading && roleRows.length === 0) || (roleRows.length === 0 && !sessionVerified)) {
    return <RouteFallback label="Checking access..." />;
  }

  if (isBanned && role !== "admin") {
    return <RouteFallback label="Redirecting..." />;
  }

  if (allowedRole === "admin") {
    if (role !== "admin") {
      return <RouteFallback label="Redirecting..." />;
    }
    return <>{children}</>;
  }

  if (allowedRole === "caterer" && access.allowed && access.approvalStatus === "pending") {
    return <>{children}</>;
  }

  if (
    allowedRole === "pfa" &&
    access.allowed &&
    (access.approvalStatus === "pending" || access.approvalStatus === "approved")
  ) {
    return <>{children}</>;
  }

  if (!access.allowed || access.approvalStatus !== "approved") {
    return <RouteFallback label="Redirecting..." />;
  }

  return <>{children}</>;
};
